import { useEffect, useMemo, useState } from "react";
import {
  ApiOutlined,
  BankOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  EyeOutlined,
  KeyOutlined,
  LogoutOutlined,
  MenuOutlined,
  PlusOutlined,
  SettingOutlined,
  StopOutlined,
  SyncOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Dropdown,
  Empty,
  Form,
  Input,
  InputNumber,
  Layout,
  Menu,
  Modal,
  Select,
  Segmented,
  Spin,
  Statistic,
  Table,
  Tag,
  Tabs,
  Tooltip,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { MenuProps } from "antd";
import ReactECharts from "echarts-for-react";
import { api } from "./api";
import type { APIClient, APIKey, Customer, Invoice, Model, PortalAccount, PortalUser, PriceVersion, UsageEvent } from "./api";

const { Header, Sider, Content } = Layout;

type View = "summary" | "models" | "usage" | "customers" | "clients" | "billing" | "users";

const channelLabels: Record<string, string> = {
  internal: "内部",
  "external-free": "外部免费",
  "external-paid": "外部付费",
};

const statusColor: Record<string, string> = {
  success: "green",
  failed: "red",
  cancelled: "default",
  active: "green",
  revoked: "red",
  draft: "default",
  reviewing: "gold",
  issued: "blue",
  "partially-paid": "orange",
  paid: "green",
  overdue: "red",
  void: "default",
};

const moneyFormatter = new Intl.NumberFormat("zh-CN", {
  maximumFractionDigits: 10,
  useGrouping: false,
});

function formatMoney(value: string | number | undefined) {
  if (value === undefined || value === "") return "-";
  const amount = typeof value === "number" ? value : Number(value);
  return Number.isFinite(amount) ? `¥${moneyFormatter.format(amount)}` : `¥${value}`;
}

function App() {
  const [user, setUser] = useState<PortalUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api.me().then(setUser).catch(() => setUser(null)).finally(() => setLoading(false));
  }, []);

  if (loading) return <Centered><Spin size="large" /></Centered>;
  if (!user) return <Login onSuccess={setUser} />;

  return <Portal user={user} onUserChange={setUser} onLogout={() => {
    setError("");
    api.logout().catch((reason: Error) => setError(reason.message)).finally(() => setUser(null));
  }} error={error} />;
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="centered">{children}</div>;
}

function Login({ onSuccess }: { onSuccess: (user: PortalUser) => void }) {
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  return (
    <main className="login-shell">
      <section className="login-panel">
        <div className="product-mark">TTD</div>
        <Typography.Title level={2}>模型网关</Typography.Title>
        <Typography.Paragraph type="secondary">用量、客户与账单管理</Typography.Paragraph>
        {error && <Alert type="error" showIcon message={error} />}
        <Form layout="vertical" onFinish={({ email, password }) => {
          setSubmitting(true);
          setError("");
          api.login(email, password).then(onSuccess).catch((reason: Error) => setError(reason.message)).finally(() => setSubmitting(false));
        }}>
          <Form.Item name="email" label="邮箱" rules={[{ required: true }, { type: "email" }]}>
            <Input autoComplete="email" />
          </Form.Item>
          <Form.Item name="password" label="密码" rules={[{ required: true }]}>
            <Input.Password autoComplete="current-password" />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={submitting} block>登录</Button>
        </Form>
      </section>
    </main>
  );
}

function Portal({ user, onUserChange, onLogout, error }: { user: PortalUser; onUserChange: (user: PortalUser) => void; onLogout: () => void; error: string }) {
  const [view, setView] = useState<View>("summary");
  const [siderCollapsed, setSiderCollapsed] = useState(false);
  const [mobileNavigation, setMobileNavigation] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const internal = user.role === "internal-admin";
  const menuItems: MenuProps["items"] = internal ? [
    { type: "group", label: "运营中心", children: [
      { key: "summary", icon: <DashboardOutlined />, label: "概览" },
      { key: "usage", icon: <DatabaseOutlined />, label: "请求记录" },
    ] },
    { type: "group", label: "客户管理", children: [
      { key: "customers", icon: <TeamOutlined />, label: "客户列表" },
      { key: "clients", icon: <KeyOutlined />, label: "API 接入" },
    ] },
    { type: "group", label: "模型管理", children: [
      { key: "models", icon: <ApiOutlined />, label: "模型目录" },
    ] },
    { type: "group", label: "财务中心", children: [
      { key: "billing", icon: <BankOutlined />, label: "定价与账单" },
    ] },
    { type: "group", label: "平台管理", children: [
      { key: "users", icon: <UserOutlined />, label: "系统用户" },
    ] },
  ] : [
    { key: "summary", icon: <DashboardOutlined />, label: "概览" },
    { key: "clients", icon: <KeyOutlined />, label: "API 接入" },
    { key: "usage", icon: <DatabaseOutlined />, label: "用量" },
    { key: "billing", icon: <BankOutlined />, label: "价格与账单" },
  ];

  return (
    <Layout className="app-layout">
      {mobileNavigation && !siderCollapsed && <button className="nav-backdrop" aria-label="关闭导航" onClick={() => setSiderCollapsed(true)} />}
      <Sider width={220} theme="light" breakpoint="lg" collapsedWidth="0" collapsed={siderCollapsed} onBreakpoint={(broken) => { setMobileNavigation(broken); setSiderCollapsed(broken); }} trigger={null} className="app-sider">
        <div className="brand"><span className="brand-symbol">TTD</span><span>模型网关</span></div>
        <Menu mode="inline" selectedKeys={[view]} items={menuItems} onSelect={({ key }) => { setView(key as View); if (mobileNavigation) setSiderCollapsed(true); }} />
      </Sider>
      <Layout>
        <Header className="app-header">
          <div className="header-identity">
            {siderCollapsed && <Button className="sider-toggle" type="text" icon={<MenuOutlined />} onClick={() => setSiderCollapsed(false)} aria-label="打开导航" />}
            <Typography.Text strong>{user.customer_name ?? "内部运营"}</Typography.Text>
          </div>
          <Dropdown menu={{ items: [
            { key: "profile", icon: <UserOutlined />, label: "个人中心", onClick: () => setProfileOpen(true) },
            { type: "divider" },
            { key: "logout", icon: <LogoutOutlined />, label: "退出", onClick: onLogout },
          ] }} trigger={["click"]}>
            <Button type="text" icon={<UserOutlined />} aria-label="账号菜单">{user.display_name}</Button>
          </Dropdown>
        </Header>
        <Content className="app-content">
          {error && <Alert type="error" message={error} showIcon closable />}
          {user.commercial_mode === "shadow" && <Alert className="mode-alert" type="info" showIcon message="费用试算" description="当前仅记录用量并展示试算金额，不扣费、不产生应付金额。" />}
          {view === "summary" && <SummaryView trialMode={user.commercial_mode === "shadow"} />}
          {view === "models" && <ModelsView />}
          {view === "usage" && <UsageView />}
          {view === "customers" && internal && <CustomersView />}
          {view === "users" && internal && <UsersView />}
          {view === "clients" && <ClientsView internal={internal} />}
          {view === "billing" && <BillingView internal={internal} formalBilling={user.commercial_mode === "formal"} />}
        </Content>
      </Layout>
      <ProfileModal open={profileOpen} user={user} onClose={() => setProfileOpen(false)} onUserChange={onUserChange} onLogout={onLogout} />
    </Layout>
  );
}

function ProfileModal({ open, user, onClose, onUserChange, onLogout }: { open: boolean; user: PortalUser; onClose: () => void; onUserChange: (user: PortalUser) => void; onLogout: () => void }) {
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  return <Modal className="scrollable-form-modal" title="个人中心" open={open} onCancel={onClose} footer={null} destroyOnHidden width={520} afterOpenChange={(visible) => {
    if (visible) profileForm.setFieldsValue({ email: user.email, display_name: user.display_name });
  }}>
    <Tabs items={[
      { key: "profile", label: "基础信息", children: <Form name="profile-form" form={profileForm} layout="vertical" onFinish={(values) => api.updateProfile(values).then((updated) => { onUserChange(updated); message.success("个人资料已更新"); }).catch((reason: Error) => message.error(reason.message))}>
        <Form.Item name="display_name" label="显示名称" rules={[{ required: true }]}><Input /></Form.Item>
        <Form.Item name="email" label="登录邮箱" rules={[{ required: true }, { type: "email" }]}><Input autoComplete="email" /></Form.Item>
        <Form.Item name="current_password" label="当前密码" extra="修改登录邮箱时需要验证" rules={[{ min: 1 }]}><Input.Password autoComplete="current-password" /></Form.Item>
        <Button type="primary" htmlType="submit">保存资料</Button>
      </Form> },
      { key: "password", label: "修改密码", children: <Form name="password-form" form={passwordForm} layout="vertical" onFinish={({ current_password, new_password }) => api.changePassword({ current_password, new_password }).then(() => { message.success("密码已修改，请重新登录"); passwordForm.resetFields(); onClose(); onLogout(); }).catch((reason: Error) => message.error(reason.message))}>
        <Form.Item name="current_password" label="当前密码" rules={[{ required: true }]}><Input.Password autoComplete="current-password" /></Form.Item>
        <Form.Item name="new_password" label="新密码" rules={[{ required: true, min: 12 }]}><Input.Password autoComplete="new-password" /></Form.Item>
        <Form.Item name="confirm_password" label="确认新密码" dependencies={["new_password"]} rules={[{ required: true }, ({ getFieldValue }) => ({ validator: (_, value) => value === getFieldValue("new_password") ? Promise.resolve() : Promise.reject(new Error("两次密码不一致")) })]}><Input.Password autoComplete="new-password" /></Form.Item>
        <Button type="primary" htmlType="submit">修改密码</Button>
      </Form> },
    ]} />
  </Modal>;
}

function PageHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return <header className="page-header"><Typography.Title level={3}>{title}</Typography.Title><Typography.Text type="secondary">{subtitle}</Typography.Text></header>;
}

function SummaryView({ trialMode }: { trialMode: boolean }) {
  const { data, loading, error } = useLoad(api.summary);
  const chart = useMemo(() => ({
    tooltip: { trigger: "axis" },
    legend: { data: ["字符数", "VC 秒数", "金额"] },
    grid: { left: 48, right: 48, top: 48, bottom: 28 },
    xAxis: { type: "category", data: (data?.daily_usage ?? []).map((item) => item.date) },
    yAxis: [{ type: "value", name: "用量" }, { type: "value", name: "元" }],
    series: [
      { name: "字符数", type: "bar", data: (data?.daily_usage ?? []).map((item) => item.characters), itemStyle: { color: "#176b55" } },
      { name: "VC 秒数", type: "bar", data: (data?.daily_usage ?? []).map((item) => item.seconds), itemStyle: { color: "#b76e00" } },
      { name: "金额", type: "line", yAxisIndex: 1, smooth: true, data: (data?.daily_usage ?? []).map((item) => item.amount), itemStyle: { color: "#b54708" } },
    ],
  }), [data]);
  if (loading) return <Spin />;
  if (error) return <Alert type="error" message={error} showIcon />;
  if (!data) return <Empty />;
  return <>
    <PageHeader title="用量概览" subtitle="当前自然月，Asia/Shanghai" />
    <div className="metric-grid">
      <Card><Statistic title="生成字符" value={data.characters_this_month} /></Card>
      <Card><Statistic title="VC 使用秒数" value={data.seconds_this_month} /></Card>
      <Card><Statistic title={trialMode ? "试算金额" : "计费金额"} value={data.amount_this_month} prefix="¥" /></Card>
      <Card><Statistic title="成功请求" value={data.successful_requests} /></Card>
      <Card><Statistic title="失败率" value={data.failure_rate * 100} precision={2} suffix="%" /></Card>
    </div>
    <section className="content-section"><Typography.Title level={4}>每日趋势</Typography.Title><ReactECharts option={chart} className="usage-chart" /></section>
    <section className="content-section"><Typography.Title level={4}>最近请求</Typography.Title><UsageTable data={data.recent_usage ?? []} /></section>
  </>;
}

function ModelsView() {
  const { data = [], loading, error } = useLoad(api.models);
  const columns: ColumnsType<Model> = [
    { title: "名称", dataIndex: "label", width: 190, render: (value, item) => <div className="model-name"><strong>{value}</strong><Typography.Text code>{item.id}</Typography.Text></div> },
    { title: "类型", dataIndex: "kind", width: 90, render: (value) => <Tag color={value === "tts" ? "blue" : "gold"}>{value.toUpperCase()}</Tag> },
    { title: "输出", dataIndex: "output_formats", width: 130, render: (items: string[]) => items.map((item) => <Tag key={item}>{item.toUpperCase()}</Tag>) },
    { title: "扩展能力", dataIndex: "extensions", render: (items: Model["extensions"]) => <span className="tag-list">{items.map((item) => <Tag key={item.name}>{item.name}</Tag>)}</span> },
    { title: "配置版本", dataIndex: "config_version", width: 180 },
  ];
  return <><PageHeader title="模型目录" subtitle="调用时使用 Model ID；展示名称不参与路由" />{error && <Alert type="error" message={error} showIcon />}<Table rowKey="id" loading={loading} columns={columns} dataSource={data} pagination={false} scroll={{ x: 1120 }} /></>;
}

function UsageView() {
  const { data = [], loading, error } = useLoad(api.usage);
  return <><PageHeader title="请求用量" subtitle="成功和失败请求均留痕；ASR 当前只审计不计费，付费 TTS 按字符，VC 与分轨按毫秒精度的音频时长计费" />{error && <Alert type="error" message={error} showIcon />}<UsageTable data={data} loading={loading} /></>;
}

function UsageTable({ data, loading = false }: { data: UsageEvent[]; loading?: boolean }) {
  const columns: ColumnsType<UsageEvent> = [
    { title: "完成时间", dataIndex: "completed_at", width: 190, render: (value) => new Date(value).toLocaleString("zh-CN") },
    { title: "请求 ID", dataIndex: "request_id", width: 180, ellipsis: true },
    { title: "客户 / Client", key: "actor", render: (_, item) => <div>{item.customer_name ?? "内部"}<br /><Typography.Text type="secondary">{item.client_name ?? "-"}</Typography.Text></div> },
    { title: "API Key", dataIndex: "key_prefix", width: 150, render: (value) => value ? <Typography.Text code>{value}</Typography.Text> : <Typography.Text type="secondary">历史未知</Typography.Text> },
    { title: "渠道", dataIndex: "channel", width: 110, render: (value) => channelLabels[value] ?? value },
    { title: "模型", dataIndex: "model_id", width: 120 },
    { title: "模态", dataIndex: "modality", width: 90, render: (value) => value.toUpperCase() },
    { title: "结果", dataIndex: "outcome", width: 90, render: (value) => <Tag color={statusColor[value]}>{value}</Tag> },
    { title: "字符", dataIndex: "billable_characters", align: "right", width: 90 },
    { title: "计费时长", dataIndex: "billable_duration_ms", align: "right", width: 110, render: (value) => value > 0 ? `${(value / 1000).toFixed(3)} s` : "-" },
    { title: "金额", dataIndex: "amount", align: "right", width: 140, render: (value, item) => value ? <span>{formatMoney(value)}{item.shadow && <Tag color="blue" className="shadow-tag">试算</Tag>}</span> : "-" },
  ];
  return <Table rowKey="id" size="middle" loading={loading} columns={columns} dataSource={data} scroll={{ x: 1200 }} />;
}

function CustomersView() {
  const { data = [], loading, error, reload } = useLoad(api.customers);
  const { data: models = [] } = useLoad(api.models);
  const [open, setOpen] = useState(false);
  const [grantCustomer, setGrantCustomer] = useState<Customer | null>(null);
  const [form] = Form.useForm();
  const [grantForm] = Form.useForm();
  const columns: ColumnsType<Customer> = [
    { title: "客户", key: "customer", render: (_, item) => <div><strong>{item.name}</strong><br /><Typography.Text code>{item.code}</Typography.Text></div> },
    { title: "唯一登录用户", key: "admin", render: (_, item) => item.admin_email ? <div>{item.admin_display_name}<br /><Typography.Text type="secondary">{item.admin_email}</Typography.Text></div> : <Typography.Text type="secondary">内部客户无客户登录</Typography.Text> },
    { title: "模型授权", dataIndex: "grants", render: (items: string[]) => items.map((item) => <Tag key={item}>{item === "*" ? "全部模型" : item}</Tag>) },
    { title: "状态", dataIndex: "status", render: (value) => <Tag color={statusColor[value]}>{value}</Tag> },
    { title: "计费生效", dataIndex: "billing_enabled_at", render: (value) => value ? new Date(value).toLocaleString("zh-CN") : "未启用" },
    { title: "操作", key: "actions", width: 110, render: (_, item) => <Button type="text" icon={<SettingOutlined />} disabled={item.code === "ttd-internal"} onClick={() => { setGrantCustomer(item); grantForm.setFieldsValue({ grants: item.grants }); }}>模型授权</Button> },
  ];
  return <>
    <div className="page-title-row"><PageHeader title="客户列表" subtitle="客户、唯一登录用户、模型授权和计费边界" /><Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>新建客户</Button></div>
    {error && <Alert type="error" message={error} showIcon />}
    <Table rowKey="id" loading={loading} columns={columns} dataSource={data} scroll={{ x: 1100 }} />
    <Modal className="scrollable-form-modal" title="新建客户与登录用户" open={open} onCancel={() => setOpen(false)} onOk={() => form.submit()} destroyOnHidden>
      <Form form={form} layout="vertical" onFinish={(values) => api.createCustomer({ ...values, billing_enabled_at: values.billing_enabled_at?.toISOString() }).then(() => { message.success("客户已创建"); setOpen(false); form.resetFields(); reload(); }).catch((reason: Error) => message.error(reason.message))}>
        <Form.Item name="name" label="客户名称" rules={[{ required: true }]}><Input /></Form.Item>
        <Form.Item name="code" label="客户代码" rules={[{ required: true, pattern: /^[a-z0-9][a-z0-9-]{1,62}$/ }]}><Input placeholder="acme" /></Form.Item>
        <Form.Item name="admin_display_name" label="管理员姓名" rules={[{ required: true }]}><Input /></Form.Item>
        <Form.Item name="admin_email" label="管理员登录邮箱" rules={[{ required: true }, { type: "email" }]}><Input autoComplete="off" /></Form.Item>
        <Form.Item name="admin_password" label="初始密码" rules={[{ required: true, min: 12 }]}><Input.Password autoComplete="new-password" /></Form.Item>
        <Form.Item name="grants" label="客户模型授权" rules={[{ required: true }]}><Select mode="multiple" options={models.map((item) => ({ value: item.id, label: `${item.label} (${item.id})` }))} /></Form.Item>
        <Form.Item name="billing_enabled_at" label="计费生效时间"><DatePicker showTime className="full-width" /></Form.Item>
      </Form>
    </Modal>
    <Modal title={`模型授权 · ${grantCustomer?.name ?? ""}`} open={Boolean(grantCustomer)} onCancel={() => setGrantCustomer(null)} onOk={() => grantForm.submit()} destroyOnHidden>
      <Form form={grantForm} layout="vertical" onFinish={({ grants }) => grantCustomer && api.updateCustomerGrants(grantCustomer.id, grants).then(() => { message.success("客户模型授权已更新"); setGrantCustomer(null); reload(); }).catch((reason: Error) => message.error(reason.message))}>
        <Form.Item name="grants" label="允许模型" rules={[{ required: true }]}><Select mode="multiple" options={models.map((item) => ({ value: item.id, label: `${item.label} (${item.id})` }))} /></Form.Item>
      </Form>
    </Modal>
  </>;
}

function UsersView() {
  const { data = [], loading, error } = useLoad(api.users);
  const columns: ColumnsType<PortalAccount> = [
    { title: "姓名", dataIndex: "display_name" },
    { title: "邮箱", dataIndex: "email" },
    { title: "角色", dataIndex: "role", render: () => "系统管理员" },
    { title: "状态", dataIndex: "status", render: (value) => <Tag color={statusColor[value]}>{value}</Tag> },
  ];
  return <>
    <PageHeader title="系统用户" subtitle="跨客户平台身份；客户登录用户在客户列表中维护" />
    {error && <Alert type="error" message={error} showIcon />}
    <Table rowKey="id" loading={loading} columns={columns} dataSource={data} />
  </>;
}

function ClientsView({ internal }: { internal: boolean }) {
  const { data = [], loading, error, reload } = useLoad(api.clients);
  const { data: customers = [] } = useLoad(internal ? api.customers : emptyCustomers);
  const { data: models = [] } = useLoad(api.models);
  const [open, setOpen] = useState(false);
  const [keyClient, setKeyClient] = useState<APIClient | null>(null);
  const [rotateKey, setRotateKey] = useState<APIKey | null>(null);
  const [issuedToken, setIssuedToken] = useState<{ title: string; token: string } | null>(null);
  const [expandedClientIDs, setExpandedClientIDs] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [keyForm] = Form.useForm();
  const [rotateForm] = Form.useForm();
  const channel = Form.useWatch("channel", form) ?? "external-free";
  const customerID = Form.useWatch("customer_id", form);
  const selectedCustomer = customers.find((item) => item.id === customerID);
  const permittedModels = channel === "internal" || !selectedCustomer ? models : models.filter((item) => selectedCustomer.grants.includes(item.id));
  const showToken = (title: string, token: string) => setIssuedToken({ title, token });
  const toggleClientKeys = (clientID: string) => setExpandedClientIDs((current) => current.includes(clientID) ? current.filter((id) => id !== clientID) : [...current, clientID]);
  const keyColumns: ColumnsType<APIKey> = [
    { title: "Key 前缀", dataIndex: "prefix", render: (value) => <Typography.Text code>{value}</Typography.Text> },
    { title: "状态", dataIndex: "status", width: 90, render: (value) => <Tag color={statusColor[value]}>{value}</Tag> },
    { title: "创建时间", dataIndex: "created_at", width: 190, render: (value) => new Date(value).toLocaleString("zh-CN") },
    { title: "最后使用", dataIndex: "last_used_at", width: 190, render: (value) => value ? new Date(value).toLocaleString("zh-CN") : "尚未使用" },
    { title: "有效期", dataIndex: "expires_at", width: 190, render: (value) => value ? new Date(value).toLocaleString("zh-CN") : "长期有效" },
    { title: "操作", key: "actions", width: internal ? 132 : 88, render: (_: unknown, key: APIKey) => <div className="key-actions">
      <Tooltip title={key.recoverable ? "查看并复制 API Key" : "旧 Key 无法显示，请直接轮换为新 Key"}><Button type="text" icon={<EyeOutlined />} disabled={!key.recoverable} onClick={() => api.revealAPIKey(key.id).then((result) => showToken("API Key", result.token)).catch((reason: Error) => message.error(reason.message))} aria-label="查看 API Key" /></Tooltip>
      <Tooltip title={key.recoverable ? "轮换 API Key" : "替换为可查看的新 Key"}><Button type="text" icon={<SyncOutlined />} disabled={key.status !== "active"} onClick={() => { setRotateKey(key); rotateForm.resetFields(); }} aria-label="轮换 API Key" /></Tooltip>
      {internal && <Tooltip title="吊销 API Key"><Button danger type="text" icon={<StopOutlined />} disabled={key.status !== "active"} onClick={() => Modal.confirm({
        title: "吊销 API Key",
        content: "仅此 Key 立即失效，Client 和其他 Key 不受影响。共享内部 Client 必须始终保留至少一个有效 Key。",
        okButtonProps: { danger: true },
        onOk: () => api.revokeAPIKey(key.id).then(() => { message.success("API Key 已吊销"); reload(); }).catch((reason: Error) => message.error(reason.message)),
      })} aria-label="吊销 API Key" /></Tooltip>}
    </div> },
  ];
  const columns: ColumnsType<APIClient> = [
    { title: "名称", dataIndex: "name" },
    { title: "客户", dataIndex: "customer_name" },
    { title: "渠道", dataIndex: "channel", render: (value) => channelLabels[value] ?? value },
    { title: "模型权限", dataIndex: "grants", render: (items: string[]) => items.map((item) => <Tag key={item}>{item}</Tag>) },
    { title: "API Keys", dataIndex: "keys", width: 150, render: (items: APIKey[], item) => <Button icon={<KeyOutlined />} disabled={items.length === 0} onClick={() => toggleClientKeys(item.id)}>{expandedClientIDs.includes(item.id) ? "收起 Keys" : `查看 Keys (${items.length})`}</Button> },
    { title: "状态", dataIndex: "status", render: (value) => <Tag color={statusColor[value]}>{value}</Tag> },
    ...(internal ? [{ title: "操作", key: "actions", width: 70, render: (_: unknown, item: APIClient) => <Tooltip title="停用 API Client"><Button danger type="text" icon={<StopOutlined />} disabled={item.status !== "active" || item.id === "00000000-0000-4000-8000-000000000001"} onClick={() => Modal.confirm({ title: "停用 API Client", content: "该 Client 下所有 API Key 都会立即失效。", okButtonProps: { danger: true }, onOk: () => api.revokeClient(item.id).then(() => { message.success("Client 已停用"); reload(); }) })} aria-label="停用 API Client" /></Tooltip> }] : []),
  ];
  return <>
    <div className="page-title-row"><PageHeader title="API 接入" subtitle="API Client 是应用身份，API Key 是可独立轮换的凭证" />{internal && <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>新建 Client</Button>}</div>
    {error && <Alert type="error" message={error} showIcon />}
    <Table rowKey="id" loading={loading} columns={columns} dataSource={data} scroll={{ x: 980 }} expandable={{ showExpandColumn: false, expandedRowKeys: expandedClientIDs, onExpandedRowsChange: (keys) => setExpandedClientIDs(keys.map(String)), expandedRowRender: (client) => <div className="client-keys"><div className="client-keys-header"><Typography.Text strong>{client.name} · API Keys ({client.keys.length})</Typography.Text>{internal && <Button icon={<KeyOutlined />} disabled={client.status !== "active"} onClick={() => { setKeyClient(client); keyForm.resetFields(); }}>签发 Key</Button>}</div><Table rowKey="id" size="small" columns={keyColumns} dataSource={client.keys} pagination={false} scroll={{ x: 920 }} /></div> }} />
    <Modal className="scrollable-form-modal" title="新建 API Client" open={open} onCancel={() => setOpen(false)} confirmLoading={submitting} okButtonProps={{ htmlType: "submit", form: "create-client-form" }} destroyOnHidden>
      <Form id="create-client-form" form={form} layout="vertical" initialValues={{ channel: "external-free" }} onFinish={async (values) => {
      try {
        setSubmitting(true);
        const result = await api.createClient({ ...values, expires_at: values.expires_at?.toISOString() });
        setOpen(false);
        form.resetFields();
        reload();
        showToken("首个 API Key 已签发", result.token);
      } catch (reason) {
        if (reason instanceof Error) message.error(reason.message);
      } finally {
        setSubmitting(false);
      }
      }} onFinishFailed={() => message.error("请检查必填项")} onValuesChange={(changed) => {
        if (changed.customer_id) form.setFieldValue("grants", []);
      }}>
        <Form.Item name="name" label="名称" rules={[{ required: true }]}><Input /></Form.Item>
        <Form.Item name="channel" label="渠道" rules={[{ required: true }]}><Select options={[{ value: "internal", label: "内部" }, { value: "external-free", label: "外部免费" }, { value: "external-paid", label: "外部付费" }]} /></Form.Item>
        <Form.Item noStyle shouldUpdate={(before, after) => before.channel !== after.channel}>{({ getFieldValue }) => getFieldValue("channel") !== "internal" && <Form.Item name="customer_id" label="所属客户" rules={[{ required: true }]}><Select options={customers.filter((item) => item.code !== "ttd-internal").map((item) => ({ value: item.id, label: item.name }))} /></Form.Item>}</Form.Item>
        <Form.Item name="grants" label="模型权限" rules={[{ required: true }]}><Select mode="multiple" options={permittedModels.map((item) => ({ value: item.id, label: `${item.label} (${item.id})` }))} /></Form.Item>
        <Form.Item name="expires_at" label="有效期"><DatePicker showTime className="full-width" /></Form.Item>
      </Form>
    </Modal>
    <Modal title={`签发 API Key · ${keyClient?.name ?? ""}`} open={Boolean(keyClient)} onCancel={() => setKeyClient(null)} onOk={() => keyForm.submit()} destroyOnHidden>
      <Form form={keyForm} layout="vertical" onFinish={({ expires_at }) => keyClient && api.issueAPIKey(keyClient.id, expires_at?.toISOString()).then((result) => { setKeyClient(null); reload(); showToken("API Key 已签发", result.token); }).catch((reason: Error) => message.error(reason.message))}>
        <Form.Item name="expires_at" label="有效期"><DatePicker showTime className="full-width" /></Form.Item>
      </Form>
    </Modal>
    <Modal title="轮换 API Key" open={Boolean(rotateKey)} onCancel={() => setRotateKey(null)} onOk={() => rotateForm.submit()} okText="确认轮换" destroyOnHidden>
      <Alert type="warning" showIcon message={rotateKey?.recoverable ? "新 Key 创建后，当前 Key 会立即失效" : "旧 Key 无法显示；替换后旧 Key 立即失效，并展示新的可管理 Key"} />
      <Form form={rotateForm} layout="vertical" onFinish={({ expires_at }) => rotateKey && api.rotateAPIKey(rotateKey.id, expires_at?.toISOString()).then((result) => { setRotateKey(null); reload(); showToken("API Key 已轮换", result.token); }).catch((reason: Error) => message.error(reason.message))}>
        <Form.Item name="expires_at" label="新有效期" extra="不填写则继承当前 Key 的有效期；已过期 Key 请设置未来时间"><DatePicker showTime className="full-width" /></Form.Item>
      </Form>
    </Modal>
    <Modal title={issuedToken?.title} open={Boolean(issuedToken)} onCancel={() => setIssuedToken(null)} onOk={() => setIssuedToken(null)} cancelButtonProps={{ style: { display: "none" } }} destroyOnHidden width={560}>
      <Alert type="info" showIcon message="可随时在 API 接入页面再次查看和复制" />
      <Typography.Paragraph copyable code className="token-output">{issuedToken?.token}</Typography.Paragraph>
    </Modal>
  </>;
}

function BillingView({ internal, formalBilling }: { internal: boolean; formalBilling: boolean }) {
  const invoices = useLoad(api.invoices);
  const prices = useLoad(api.prices);
  const { data: customers = [] } = useLoad(internal ? api.customers : emptyCustomers);
  const [priceOpen, setPriceOpen] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [priceForm] = Form.useForm();
  const [invoiceForm] = Form.useForm();
  const invoiceColumns: ColumnsType<Invoice> = [
    { title: "客户", dataIndex: "customer_name" },
    { title: "账期", key: "period", render: (_, item) => `${item.period_start} 至 ${item.period_end}` },
    { title: "应收", dataIndex: "total_amount", align: "right", render: formatMoney },
    { title: "已收", dataIndex: "paid_amount", align: "right", render: formatMoney },
    { title: "类型", dataIndex: "shadow", render: (value) => value ? <Tag color="blue">账单预览</Tag> : <Tag color="green">正式账单</Tag> },
    { title: "状态", dataIndex: "status", render: (value) => <Tag color={statusColor[value]}>{value}</Tag> },
  ];
  const priceColumns: ColumnsType<PriceVersion> = [
    { title: "客户", dataIndex: "customer_name" },
    { title: "计价类型", dataIndex: "modality", render: (value) => value === "vc" ? "VC 秒" : "TTS 字符" },
    { title: "单位价格", dataIndex: "unit_price", render: (value, item) => `${formatMoney(value)} / ${item.modality === "tts" ? "字符" : "秒"}` },
    { title: "生效时间", dataIndex: "effective_from", render: (value) => new Date(value).toLocaleString("zh-CN") },
    { title: "结束时间", dataIndex: "effective_until", render: (value) => value ? new Date(value).toLocaleString("zh-CN") : "持续有效" },
    { title: "类型", dataIndex: "shadow", render: (value) => value ? <Tag color="blue">费用试算</Tag> : <Tag color="green">正式计价</Tag> },
  ];
  return <>
    <PageHeader title="价格与账单" subtitle="按 Asia/Shanghai 自然月后付费" />
    {(invoices.error || prices.error) && <Alert type="error" message={invoices.error || prices.error} showIcon />}
    <Tabs items={[
      { key: "invoices", label: "账单", children: <><div className="table-toolbar">{internal && <Button type="primary" icon={<PlusOutlined />} onClick={() => setInvoiceOpen(true)}>生成账单</Button>}</div><Table rowKey="id" loading={invoices.loading} columns={invoiceColumns} dataSource={invoices.data ?? []} /></> },
      { key: "prices", label: "价格版本", children: <><div className="table-toolbar">{internal && <Button type="primary" icon={<PlusOutlined />} onClick={() => setPriceOpen(true)}>新建价格</Button>}</div><Table rowKey="id" loading={prices.loading} columns={priceColumns} dataSource={prices.data ?? []} /></> },
    ]} />
    <Modal title="新建价格" open={priceOpen} onCancel={() => setPriceOpen(false)} onOk={() => priceForm.submit()} destroyOnHidden>
      <Form form={priceForm} layout="vertical" initialValues={{ billing_type: formalBilling ? "formal" : "shadow", modality: "tts" }} onFinish={(values) => api.createPrice({ customer_id: values.customer_id, modality: values.modality, unit_price: String(values.unit_price), effective_from: values.effective_from.toISOString(), effective_until: values.effective_until?.toISOString(), shadow: values.billing_type === "shadow" }).then(() => { message.success(values.billing_type === "shadow" ? "试算价格已创建" : "正式价格已创建"); setPriceOpen(false); priceForm.resetFields(); prices.reload(); }).catch((reason: Error) => message.error(reason.message))}>
        <Form.Item name="customer_id" label="客户" rules={[{ required: true }]}><Select options={customers.map((item) => ({ value: item.id, label: item.name }))} /></Form.Item>
        <Form.Item name="modality" label="计价类型" rules={[{ required: true }]}><Select options={[{ value: "tts", label: "TTS · 元/字符" }, { value: "vc", label: "VC · 元/秒" }, { value: "separation", label: "分轨 · 元/秒" }]} /></Form.Item>
        <Form.Item name="unit_price" label="单位价格（元）" rules={[{ required: true }]}><InputNumber min={0} precision={10} className="full-width" /></Form.Item>
        <Form.Item name="effective_from" label="生效时间" rules={[{ required: true }]}><DatePicker showTime className="full-width" /></Form.Item>
        <Form.Item name="effective_until" label="结束时间"><DatePicker showTime className="full-width" /></Form.Item>
        <Form.Item name="billing_type" label="价格类型"><Segmented block options={[{ label: "正式计价", value: "formal", disabled: !formalBilling }, { label: "费用试算", value: "shadow" }]} /></Form.Item>
      </Form>
    </Modal>
    <Modal title="生成账单" open={invoiceOpen} onCancel={() => setInvoiceOpen(false)} onOk={() => invoiceForm.submit()} destroyOnHidden>
      <Form form={invoiceForm} layout="vertical" initialValues={{ billing_type: formalBilling ? "formal" : "shadow" }} onFinish={(values) => api.generateInvoice({ customer_id: values.customer_id, period: values.period.format("YYYY-MM"), shadow: values.billing_type === "shadow" }).then(() => { message.success(values.billing_type === "shadow" ? "账单预览已更新" : "正式账单已生成"); setInvoiceOpen(false); invoiceForm.resetFields(); invoices.reload(); }).catch((reason: Error) => message.error(reason.message))}>
        <Form.Item name="customer_id" label="客户" rules={[{ required: true }]}><Select options={customers.map((item) => ({ value: item.id, label: item.name }))} /></Form.Item>
        <Form.Item name="period" label="账期" rules={[{ required: true }]}><DatePicker picker="month" className="full-width" /></Form.Item>
        <Form.Item name="billing_type" label="账单类型"><Segmented block options={[{ label: "正式账单", value: "formal", disabled: !formalBilling }, { label: "账单预览", value: "shadow" }]} /></Form.Item>
      </Form>
    </Modal>
  </>;
}

function useLoad<T>(loader: () => Promise<T>) {
  const [data, setData] = useState<T>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [version, setVersion] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    loader().then((value) => active && setData(value)).catch((reason: Error) => active && setError(reason.message)).finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [loader, version]);
  return { data, loading, error, reload: () => setVersion((value) => value + 1) };
}

async function emptyCustomers(): Promise<Customer[]> { return []; }

export default App;
